import { motion } from "framer-motion";
import { MessageCircle, Bot, UserCheck, Database } from "lucide-react";
import { LANDING } from "@/constants/testIds";

const steps = [
  {
    number: "01",
    icon: MessageCircle,
    title: "El usuario escribe a WhatsApp",
    description:
      "El lead o socio envía un mensaje a la línea única de WhatsApp de AEROBIC. No importa la hora ni el día.",
    color: "bg-sky-50 border-sky-200",
    iconColor: "text-[#38BDF8]",
    iconBg: "bg-sky-100",
  },
  {
    number: "02",
    icon: Bot,
    title: "La IA responde y califica",
    description:
      "El asistente entiende la consulta, responde con información precisa sobre planes, horarios y sedes, y califica el nivel de interés.",
    color: "bg-white border-gray-200",
    iconColor: "text-[#38BDF8]",
    iconBg: "bg-sky-50",
  },
  {
    number: "03",
    icon: UserCheck,
    title: "Derivación al asesor indicado",
    description:
      "Cuando es necesario o el usuario lo pide, el sistema asigna la conversación al asesor correcto y le envía una notificación por WhatsApp con el link.",
    color: "bg-white border-gray-200",
    iconColor: "text-[#38BDF8]",
    iconBg: "bg-sky-50",
  },
  {
    number: "04",
    icon: Database,
    title: "Todo se registra en ABC EVO",
    description:
      "Cada interacción queda guardada automáticamente en el CRM. El equipo tiene trazabilidad completa y puede continuar el proceso desde ABC EVO.",
    color: "bg-sky-50 border-sky-200",
    iconColor: "text-[#38BDF8]",
    iconBg: "bg-sky-100",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="como-funciona"
      data-testid={LANDING.howItWorksSection}
      className="bg-[#F9FAFB] py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-[#0EA5E9] text-sm font-semibold tracking-widest uppercase mb-3">
            Cómo funciona
          </span>
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-700 text-gray-900 tracking-tight">
            Un flujo ágil, sin fricciones
          </h2>
          <p className="mt-3 text-gray-500 text-base max-w-xl mx-auto">
            Cada consulta sigue un proceso claro y trazable: desde el primer
            mensaje hasta el registro en el CRM, sin pasos manuales ni
            información perdida.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {/* Connecting line (desktop) */}
          <div className="hidden lg:block absolute top-10 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-sky-200 via-sky-300 to-sky-200 z-0" />

          {steps.map((step, i) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.12 }}
              className="relative z-10"
            >
              {/* Number + icon */}
              <div className="flex flex-col items-center text-center">
                <div className={`w-20 h-20 rounded-2xl border ${step.color} flex items-center justify-center mb-4 shadow-sm`}>
                  <div className={`w-10 h-10 ${step.iconBg} rounded-xl flex items-center justify-center`}>
                    <step.icon size={22} className={step.iconColor} />
                  </div>
                </div>
                <span className="font-['Outfit'] text-5xl font-800 text-gray-100 absolute -top-1 left-1/2 -translate-x-1/2 select-none pointer-events-none -z-10">
                  {step.number}
                </span>
              </div>

              {/* Content */}
              <div className="text-center mt-1">
                <h3 className="font-['Outfit'] text-base font-600 text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Feature highlight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.5 }}
          className="mt-16 grid md:grid-cols-3 gap-4"
        >
          {[
            { label: "Tiempo promedio de respuesta", value: "< 2 seg" },
            { label: "Disponibilidad del asistente", value: "24/7 · 365 días" },
            { label: "Conversaciones en simultáneo", value: "Ilimitadas" },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-white border border-gray-200 rounded-xl p-5 text-center">
              <p className="font-['Outfit'] text-2xl font-700 text-[#38BDF8]">{kpi.value}</p>
              <p className="text-gray-500 text-sm mt-1">{kpi.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
